import { type ComponentType, lazy } from "react";

interface PageModule {
    Component: ComponentType;
}

/**
 * Declares a route's component as a `React.lazy` boundary instead of react-router's route-level
 * `lazy`.
 *
 * Route-level `lazy` exists so the router can pull a route's `loader` out of the chunk and run it
 * before committing the navigation, which means it *blocks the commit* on the chunk download:
 * `pathname` doesn't change, nothing re-renders, and no `Suspense` fallback can appear until the
 * chunk lands. Pages that only export a `Component` gain nothing from that and pay the whole cost.
 *
 * With this helper the navigation commits immediately and React awaits the chunk instead, so the
 * nearest `Suspense` boundary renders its fallback — as long as that boundary is *new* for this
 * navigation, since react-router commits inside a React transition and a transition that suspends
 * on an already-committed boundary keeps the previous UI on screen instead of showing a fallback.
 * That is why the tab pages key their `TabPanel` on the pathname.
 *
 * Keep using route-level `lazy` for a page that co-locates a `loader`, an `ErrorBoundary` or a
 * `handle` in its own module.
 */
export function lazyRoute(importPage: () => Promise<PageModule>) {
    return lazy(() => importPage().then(module => ({ default: module.Component })));
}
